<template>
  <div id="OverviewDetailsPane">
    <div
      v-if="!['noData', 'notCreated', 'notPublished'].includes(actionPlanStatus)"
      class="data"
    >
      <div
        v-if="$mq === 'laptop' || $mq === 'desktop'"
        class="bigGroup"
      >
        <div
          v-for="(group, key) in actionPlan"
          :key="key"
          class="actionPlanGroup"
        >
          <div
            v-if="key !== 'total_avg' && group.length"
            class="actionPlanDivider"
          >
            <div class="title">
              <div class="groupTitle defaultCursor">
                {{ key }}
              </div>
            </div>

            <div class="actionPlans">
              <el-row
                type="flex"
                class="actionPlanLine"
                :gutter="20"
              >
                <el-col
                  v-for="(action, fKey) in group"
                  :key="fKey"
                  class="content"
                  :span="responsiveCol"
                >
                  <div class="actionPercent defaultCursor">
                    <el-progress
                      v-if="action.avg >= 0"
                      type="circle"
                      :percentage="action.avg < 1 ? Math.round(action.avg * 100) : 100"
                      :color="color"
                      :stroke-width="15"
                      :width="100"
                    />
                    <el-progress
                      v-else
                      type="circle"
                      :percentage="0"
                      :color="color"
                      :stroke-width="15"
                      :width="100"
                    />
                  </div>
                  <div class="actionDesc defaultCursor">
                    {{ action.description }}
                  </div>
                </el-col>
              </el-row>
            </div>
          </div>
        </div>
      </div>

      <div
        v-else
        class="smallGroup"
      >
        <el-card
          v-if="actionPlan"
          id="mobileCard"
          shadow="hover"
          class="clear-card actionPlans"
          :body-style="{padding: '20px'}"
        >
          <div
            v-for="(group, key) in actionPlan"
            :key="key"
            class="actionPlanGroup"
          >
            <div
              v-if="group && group.length"
              class="actionPlanDivider"
            >
              <div class="title">
                <div class="groupTitle">
                  {{ key }}
                </div>
              </div>

              <div class="actionPlans">
                <el-row
                  type="flex"
                  class="actionPlanLine"
                  :gutter="20"
                >
                  <el-col
                    v-for="(action, fKey) in group"
                    :key="fKey"
                    class="content"
                    :span="responsiveCol"
                  >
                    <div class="actionPercent">
                      <el-progress
                        v-if="action.avg >= 0"
                        type="circle"
                        :percentage="action.avg < 1 ? Math.round(action.avg * 100) : 100"
                        :color="color"
                        :stroke-width="15"
                        :width="100"
                      />
                      <el-progress
                        v-else
                        type="circle"
                        :percentage="0"
                        :color="color"
                        :stroke-width="15"
                        :width="100"
                      />
                    </div>
                    <div class="actionDesc">
                      {{ action.description }}
                    </div>
                  </el-col>
                </el-row>
              </div>
            </div>
          </div>
        </el-card>
      </div>
    </div>
    <div
      v-else
      class="noData defaultCursor"
      :class="['mobile', 'tablet'].includes($mq) ? 'mobileMode' : 'deskMode'"
    >
      {{ placeholderText }}
    </div>
  </div>
</template>

<script>
export default {
  props: {
    actionPlanStatus: {
      type: String,
      default: 'noData',
    },

    actionPlan: {
      type: Object,
      default: null,
    },

    color: {
      type: String,
      default: '',
    },
  },

  data: () => ({
    inner: window.innerWidth,
  }),

  computed: {
    responsiveCol() {
      let col = 24;
      if (this.$mq === 'mobile') {
        col = 12;
      } else if (this.$mq === 'tablet') {
        if (this.inner < 550) {
          col = 12;
        } else {
          col = 8;
        }
      } else {
        col = 6;
      }
      return col;
    },

    placeholderText() {
      let placeholder = this.$t('common.noData');
      if (this.actionPlanStatus === 'notCreated') {
        placeholder = this.$t('actionPlan.noCreated');
      } else if (this.actionPlanStatus === 'notPublished') {
        placeholder = this.$t('actionPlan.noCreated');
      }
      return placeholder;
    },
  },

  mounted() {
    window.addEventListener('resize', this.updateInner);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.updateInner);
  },

  methods: {
    updateInner() {
      this.inner = window.innerWidth;
    },
  },
};
</script>

<style lang="scss" scoped>
  #OverviewDetailsPane {
    text-align: left;
    padding: 10px 0;

    .data {
      .actionPlanGroup {
        .title {
          display: flex;
          justify-content: space-between;
          font-weight: bold;
          margin-bottom: 10px;
          text-transform: capitalize;
          padding: 20px 5px 5px;
        }

        .actionPlans {
          .actionPlanLine {
            padding-bottom: 15px;
            border-bottom: 0.5px solid #c4c4c4;
            flex-wrap: wrap;
            align-items: stretch;

            .content {
              padding: 20px 5%;

              .actionPercent {
                width: 100%;
                text-align: center;
                padding-bottom: 20px;
              }

              .actionDesc {
                text-align: center;
                width: 100%;
                word-break: break-word;
              }
            }
          }
        }

        &:first-child {
          .title {
            padding: 5px;
          }
        }

        &:last-child {
          .actionPlanLine {
            border-bottom: none;
          }
        }
      }
    }

    #mobileCard {
      margin-bottom: 15px;

      .title {
        padding: 10px;
      }
    }

    .noData {
      text-align: center;
      min-height: 400px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      font-style: italic;

      &.mobileMode {
        background: rgba(255, 255, 255, 0.2);
        border-radius: 10px;
        color: var(--themeFontColor);
      }
    }
  }
</style>

<style lang="scss">
  #OverviewDetailsPane {
    .actionPercent {
      svg {
        path {
          stroke-linecap: butt;
        }

        path:first-child {
          stroke: #a1a1a1;
        }
      }

      .el-progress__text {
        color: var(--themeFontColor);
        font-size: 17px !important;
        font-weight: bold;
      }
    }
  }
</style>
